import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/app/query/query-keys";
import { queryClient } from "@/app/query/query-client";
import { clearPersistedCache } from "@/app/query/query-persistence";
import { restoreAuthToken } from "@/app/lib/auth-client";
import { track } from "@/app/observability";
import { authService } from "./auth.service";
import type { AuthStatus, SessionUserModel } from "./auth.model";

/**
 * WHO IS SIGNED IN — held by the query cache, not by a store.
 *
 * A session is server state: the service decides whether it is still valid, it expires
 * without anyone touching the app, and two parts of the UI must never disagree about it.
 * Keeping it in a hand-written store is the same mistake as keeping a list of notes there,
 * and it is the mistake that produces "one screen thinks I'm logged in".
 */
export function useSession() {
  return useQuery({
    queryKey: queryKeys.session.current(),
    queryFn: async (): Promise<SessionUserModel | null> => {
      // The token lives in the Keychain; it has to be read back before the first request.
      await restoreAuthToken();
      const result = await authService.currentUser();
      // Not signed in is an ANSWER, not a failure: returning null keeps it out of the error
      // path, where it would produce a toast on every cold start.
      return result.ok ? result.data : null;
    },
    staleTime: 5 * 60_000,
    retry: false,
    meta: { ownsErrorMessage: true },
  });
}

/**
 * The three-state answer the router guard needs. `unknown` is not decoration: on launch the
 * token is still being read out of the Keychain, and a guard that treats not-yet-known as
 * signed-out bounces every returning user to the sign-in screen for a frame.
 */
export function useAuthStatus(): { status: AuthStatus; user: SessionUserModel | null } {
  const { data, isPending } = useSession();
  if (isPending) return { status: "unknown", user: null };
  return { status: data ? "authenticated" : "anonymous", user: data ?? null };
}

export function useSignIn() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; password: string }) => {
      const result = await authService.signIn(input.email, input.password);
      // `fields` carries WHICH input was refused and why, so the form can word it beside
      // that input instead of falling back to "check the highlighted fields".
      if (!result.ok)
        throw Object.assign(new Error(result.message ?? result.error), {
          code: result.error,
          fields: result.fields,
        });
      return result.data;
    },
    // The form words its own failure — a wrong password is not a system error.
    mutationKey: queryKeys.session.signIn(),
    meta: { ownsErrorMessage: true },
    onSuccess(user) {
      client.setQueryData(queryKeys.session.current(), user);
      // Funnel events only: the steps of a flow somebody has a question about. An event
      // nobody will ever query is a cost with no answer.
      track("signed_in", { method: "password" });
    },
  });
}

export function useSignUp() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; email: string; password: string }) => {
      const result = await authService.signUp(input.name, input.email, input.password);
      if (!result.ok)
        throw Object.assign(new Error(result.message ?? result.error), {
          code: result.error,
          fields: result.fields,
        });
      return result.data;
    },
    mutationKey: queryKeys.session.signUp(),
    meta: { ownsErrorMessage: true },
    onSuccess(user) {
      client.setQueryData(queryKeys.session.current(), user);
      track("signed_up", { method: "password" });
    },
  });
}

/**
 * Social sign-in, as a MUTATION rather than a call from the button.
 *
 * The buttons used to call `authService.signInWithProvider()` straight from `onClick` and
 * drop the result on the floor. Since a service returns a failure rather than throwing one,
 * that meant a failed Apple or Google sign-in was announced ZERO times: no message, no
 * report, no spinner ending — the only such path in the app. Going through a mutation puts
 * it back under the one authority that announces a failure, once.
 *
 * No `ownsErrorMessage`: unlike email sign-in there is no field to put the message beside,
 * so the global toast is the right and only place for it.
 */
export function useSignInWithProvider() {
  return useMutation({
    mutationFn: async (provider: "apple" | "google") => {
      const result = await authService.signInWithProvider(provider);
      if (!result.ok) throw Object.assign(new Error(result.error), { code: result.error });
      return result.data;
    },
    mutationKey: queryKeys.session.signInWithProvider(),
    onSuccess(_data, provider) {
      track("signed_in", { method: provider });
    },
  });
}

/**
 * DROP EVERYTHING THAT BELONGED TO THE PREVIOUS USER, AND MAKE SURE THE APP HEARS ABOUT IT.
 *
 * The order here is the whole fix. It used to be `queryCache.clear()` followed by writing
 * the null session, and that clear DESTROYS the query object the mounted observers are
 * watching — including the one the router's auth status is derived from. Writing the null
 * afterwards created a BRAND NEW query that nobody was subscribed to, so every screen kept
 * rendering the previous answer forever: signed out on the server, signed in on screen.
 *
 * So the session answer is written FIRST, into the query observers are already watching —
 * they are notified, the status becomes "anonymous", and the router re-runs its guards.
 * Only then is the rest of the cache dropped, which is what keeps the next person to sign
 * in on this device from seeing the previous one's notes: a data leak on a shared or
 * handed-down phone, not a glitch.
 *
 * `removeQueries` rather than `clear()` for that second half, because it goes through the
 * client and notifies, instead of emptying the cache behind everyone's back.
 *
 * AND THE THIRD HALF, WHICH IS THE ONE THAT OUTLIVES THE PROCESS. The cache is now written
 * to disk so it survives being killed, which means dropping the in-memory copy is no longer
 * the whole job: the blob is still there on the next launch, and hydrating it would paint
 * the previous user's notes onto the new user's first screen before any request of theirs
 * had been sent. Awaited rather than fired off, because "the disk copy is gone" is the kind
 * of thing that must have actually happened by the time this returns.
 */
async function forgetEverythingAboutThisUser(client: QueryClient): Promise<void> {
  client.setQueryData(queryKeys.session.current(), null);
  client.removeQueries({
    predicate: (query) => query.queryKey[0] !== queryKeys.session.all[0],
  });
  await clearPersistedCache();
}

export function useSignOut() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => authService.signOut(),
    mutationKey: queryKeys.session.signOut(),
    async onSuccess() {
      /**
       * QUERIES ONLY, not the whole client. Everything CACHED belonged to that user, so it
       * goes — otherwise the next person to sign in on this device sees the previous one's
       * notes until a refetch lands, which on a shared or handed-down phone is a data leak
       * rather than a glitch. But `client.clear()` also resets every MUTATION, including the
       * one a form is currently displaying the failure of, and that made a failed sign-in
       * show nothing at all.
       *
       * The session is written AFTER the clear, because clearing removes it too, and a
       * session of `undefined` reads as "not known yet" — which sends the app back to a
       * loading spinner instead of the sign-in screen it should be showing.
       */
      await forgetEverythingAboutThisUser(client);
    },
  });
}

/**
 * Single-flight: twenty parallel requests failing at once is ONE dead session, not twenty.
 * Without this each would trigger its own round trip and its own sign-out.
 */
let verification: Promise<void> | null = null;

/**
 * A SINGLE 401 IS NOT PROOF. A phone changing cells, a proxy, a captive portal or a race
 * with a token refresh all produce one, and treating each as a dead session is what makes an
 * app "log me out at random" — a report nobody can reproduce and no screen can fix.
 *
 * So the service is asked once. Only a confirmed loss signs out; an unreachable service
 * leaves the session alone, because being offline is not being logged out.
 */
export function verifySessionOrSignOut(): Promise<void> {
  verification ??= (async () => {
    try {
      const result = await authService.currentUser();
      if (!result.ok) return;

      if (result.data) {
        queryClient.setQueryData(queryKeys.session.current(), result.data);
        return;
      }

      await authService.signOut();
      await forgetEverythingAboutThisUser(queryClient);
    } finally {
      verification = null;
    }
  })();

  return verification;
}
